/**
 * Which of an item's values can be filtered by, and what condition each one
 * stands for. The query machinery (dedupe, replace, AND-ing onto `?q=`) is
 * `utils/filterValues.js`; this is the reading of a VALUE, which needs the same
 * display helpers every read surface uses so a menu entry says what the field
 * says ("Status: Open", never "status: open-1").
 *
 * Not every value is a facet. A long note, a rich-text body and a measurement
 * are left out on purpose: no two of them are ever equal, so a filter on one
 * selects the record you are already looking at. A date filters to its DAY —
 * as the two bounds that name a day, the same pair `utils/datePresets.js`
 * writes, so it reads back as one chip.
 */
import { ELEMENT_ID_RE, SYSTEM_FIELDS, bool as makeBool, num as makeNum, str as makeStr } from '../../utils/kql';
import { conditionValueLabel, pathKey } from '../../utils/kqlBuilder';
import { dayParts } from '../../utils/datePresets';
import { cardFilterFor } from '../../utils/filterValues';
import { formatShortDate } from '../../utils/dateDisplay';
import { normalizeId } from '../../utils/ids';
import { readCurrency } from '../../utils/currency';
import { currencyLiteral } from '../../utils/money';
import { elementLabel, formatValue, getChoiceMeta } from './itemValueDisplay';
import { entryName } from '../../utils/itemTags';
import { canonicalChoices } from '../../utils/choiceValues';

const firstLookupId = value => {
	const raw = Array.isArray(value) ? value[0] : value;
	return normalizeId(raw) || (typeof raw === 'string' && raw.trim() ? raw.trim() : '');
};

const isBlank = value => (
	value === undefined || value === null || value === ''
	|| (Array.isArray(value) && !value.length)
);

/**
 * The filter one element's value stands for, or null when there is none.
 *
 *   { path, forceElement, field, label, valueLabel, conditions }
 *
 * `label` names the field and `valueLabel` the value, so a caller can write
 * both a menu entry ("Status: Open") and a record action ("Filter by Status:
 * Open") without re-deriving either.
 *
 * `facetFor(element)` (the list's vocabulary, through FilterByValueContext)
 * turns a card lookup into its FACET where one covers it — "People: Alice"
 * across every card — and leaves the field's own condition on `alternate`.
 *
 * `exact` (the cell menu and the read view's value actions, KPR-1640) asks a
 * short text, an email, a URL, a phone (as stored) or a location (its address)
 * for the value ITSELF (`=`, which the API reads
 * as an anchored, case-insensitive match) rather than for everything that
 * mentions it: "Exclude this value" as `!~` would hide rows that merely
 * contain it ("Ok" would take "Book" with it). The row's own Filter by… keeps
 * the older reading.
 */
export function valueFilterFor({ element, value, resolveLookupValue, facetFor, exact = false } = {}) {
	if (!element?.name || isBlank(value)) return null;
	const dataType = element.dataType || 'text-small';
	// The element ITSELF (element ids step 3): the value is this item's
	// card's element, so the condition names it by id — never a name another
	// card could spell, which the list would have to ask about — and replaces
	// a condition written by its name (`aliases`).
	const byId = typeof element.id === 'string' && ELEMENT_ID_RE.test(element.id);
	const base = byId
		? { path: [`#${element.id}`], forceElement: false, field: element, label: elementLabel(element), aliases: [pathKey([element.name], SYSTEM_FIELDS.includes(element.name))] }
		: { path: [element.name], forceElement: false, field: element, label: elementLabel(element) };

	switch (dataType) {
		case 'choice': {
			// A Choice that allows multiple values holds a list: the filter is
			// for its FIRST entry (`skills = welding` matches any item holding
			// welding), and the label names the entry the condition uses — the
			// lookup's rule, below.
			const one = Array.isArray(value) ? canonicalChoices(element, value)[0] : value;
			if (one === undefined || one === null || one === '') return null;
			const meta = getChoiceMeta(element, one);
			return {
				...base,
				valueLabel: meta.label || conditionValueLabel(element, one) || String(one),
				conditions: [{ cmp: 'eq', value: makeStr(String(one)) }],
			};
		}
		case 'boolean': {
			const flag = value === true || value === 'true' || value === 1;
			return {
				...base,
				valueLabel: conditionValueLabel(element, flag) || (flag ? 'Yes' : 'No'),
				conditions: [{ cmp: 'eq', value: makeBool(flag) }],
			};
		}
		case 'card-lookup': {
			// The stored id, not the title: the API matches a 24-hex value
			// against the referenced item itself, so the filter survives a
			// rename of the thing it points at.
			const id = firstLookupId(value);
			if (!id) return null;
			// The label names the ID THE CONDITION USES, not the whole
			// `allowMultiple` list: "Filter by Resident: Molly, Bob" promising
			// two and delivering `resident = <molly>` is a lie the user cannot
			// see through.
			const resolved = typeof resolveLookupValue === 'function' ? resolveLookupValue(id) : '';
			const valueLabel = resolved || id;
			const conditions = [{ cmp: 'eq', value: makeStr(id) }];
			const plain = { ...base, valueLabel, conditions };
			// THE FACET WINS when there is one. Clicking Alice on a visit means
			// "show me Alice", not "show me the visits whose `resident` field is
			// Alice" — and a collection where three differently named elements
			// point at the People card has a facet that says exactly the first
			// thing (utils/facets.js). The field's own condition stays reachable
			// as `alternate`, which the ⋯ menu offers as "only this field".
			const facet = typeof facetFor === 'function' ? facetFor(element) : null;
			if (!facet) return plain;
			return {
				...base,
				path: [...facet.path],
				forceElement: !!facet.forceElement,
				// The grammar's own flag: `@<card id> = <id>`, not an element
				// that happens to be called the same.
				facet: true,
				// `@people`, the key spelling written before F3, is this path too.
				aliases: facet.aliases || [],
				label: facet.shortLabel || facet.label,
				valueLabel,
				conditions,
				alternate: plain,
			};
		}
		case 'date':
		case 'date-time': {
			const conditions = dayParts(value);
			if (!conditions) return null;
			return { ...base, valueLabel: formatShortDate(value) || String(value), conditions };
		}
		case 'time':
			return {
				...base,
				valueLabel: formatValue(element, value) || String(value),
				conditions: [{ cmp: 'eq', value: makeStr(String(value)) }],
			};
		// A "Me too" count filters as the number it is (KPR-390): `me-too =
		// 12`. An item nobody confirmed holds no value, so offers none.
		case 'number':
		case 'decimal':
		case 'integer':
		case 'rating':
		case 'confirmations': {
			const number = Number(value);
			if (!Number.isFinite(number)) return null;
			return {
				...base,
				valueLabel: formatValue(element, value) || String(value),
				conditions: [{ cmp: 'eq', value: makeNum(number) }],
			};
		}
		case 'currency': {
			// An amount filters like a number, but always WITH its code: the
			// API refuses a bare number on a multi-currency element, and an
			// amount is only ever equal to one in the same currency.
			const money = readCurrency(value, element.options || {});
			const literal = money ? currencyLiteral(money) : '';
			if (!literal) return null;
			return {
				...base,
				valueLabel: formatValue(element, value) || literal,
				conditions: [{ cmp: 'eq', value: makeStr(literal) }],
			};
		}
		case 'phone': {
			// A phone READS one way and is STORED another: display is national
			// ("(202) 555-0123"), storage is E.164 ("+12025550123"), and the
			// API matches the stored spelling. Filtering by what the screen
			// says would empty the list every time.
			const stored = String(value ?? '').trim();
			if (!stored) return null;
			return {
				...base,
				valueLabel: formatValue(element, value) || stored,
				conditions: [{ cmp: exact ? 'eq' : 'contains', value: makeStr(stored) }],
			};
		}
		case 'location': {
			// A location is an OBJECT, and the API queries its `.address` with
			// the text rules. A value carrying only coordinates has no address
			// to match, so it is not a facet — `formatLocation`'s "37.7749,
			// -122.4194" fallback is a reading, never a query.
			const address = typeof value === 'string'
				? value.trim()
				: String(value?.address ?? '').trim();
			if (!address) return null;
			return { ...base, valueLabel: address, conditions: [{ cmp: exact ? 'eq' : 'contains', value: makeStr(address) }] };
		}
		case 'text-small':
		case 'email':
		case 'url': {
			// Text matches by CONTAINS: an address or a short line is rarely
			// typed identically twice, and "everything mentioning this" is the
			// question being asked. `exact` asks for the stored value itself.
			const text = String(formatValue(element, value) || value || '').trim();
			if (!text) return null;
			if (exact) {
				const stored = String(value ?? '').trim();
				return stored ? { ...base, valueLabel: text, conditions: [{ cmp: 'eq', value: makeStr(stored) }] } : null;
			}
			return { ...base, valueLabel: text, conditions: [{ cmp: 'contains', value: makeStr(text) }] };
		}
		case 'color': {
			// A color matches EXACTLY on its stored "#rrggbb": "every item of
			// this color" is the question, and a contains on "#1f" would find
			// a dozen blues (docs/plans/data-types-second-pass.md §4.1).
			const text = typeof value === 'string' ? value.trim() : '';
			if (!text) return null;
			return { ...base, valueLabel: text, conditions: [{ cmp: 'eq', value: makeStr(text) }] };
		}
		default:
			return null;
	}
}

/**
 * "Tag: Stomach pain" — one filter per tag on the item (docs/plans/tags.md
 * §5.2), from its chips (utils/itemTags.js itemTagEntries): `tags = <id>`,
 * which the API matches against the tag and every tag beneath it. A tag
 * the reader cannot name (a tombstone, a neutral chip) offers none, and
 * neither does a private tag the item's COLLECTION carries — on its own
 * list it would match every item. Tag filters ACCUMULATE: two of them are
 * the items carrying both.
 */
export function tagFiltersFor(entries = []) {
	const seen = new Set();
	return (Array.isArray(entries) ? entries : [])
		.filter(entry => entry && !entry.inherited && !entry.tombstone && !entry.neutral && entryName(entry))
		.filter(entry => (seen.has(entry.id) ? false : (seen.add(entry.id), true)))
		.map(entry => ({
			path: ['tags'],
			forceElement: false,
			label: 'Tag',
			valueLabel: entryName(entry),
			conditions: [{ cmp: 'eq', value: makeStr(entry.id) }],
			accumulate: true,
		}));
}

/**
 * Every filter one item offers, in the order a menu should list them: its
 * fields in card order, then its tags ("Tag: …", one per tag — `tagEntries`,
 * the chips the item shows), then its card type.
 */
export function itemValueFilters({ item, card, elementDefs = [], resolveLookupValue, facetFor, tagEntries = null } = {}) {
	const filters = (elementDefs || [])
		.map(element => valueFilterFor({
			element,
			value: item?.elements?.[element?.name],
			resolveLookupValue,
			facetFor,
		}))
		.filter(Boolean);

	filters.push(...tagFiltersFor(tagEntries));

	const cardFilter = cardFilterFor(card);
	if (cardFilter) filters.push(cardFilter);

	return filters;
}

export default valueFilterFor;
